import { useCallback, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import { Navigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { baseURL, DATAUSER } from "../../API/API";
import Anonymath from "../../Imge/anonymath.png";
import Header from "../../Components/common/Header";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faBook,
  faMapMarkerAlt,
  faSchool,
  faEnvelope,
  faPhone,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import "./Profile.css";

export default function Profile() {
  const { auth, setAuth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [profile, setProfile] = useState(auth?.user ?? null);
  const [chaptersCount, setChaptersCount] = useState(0);
  const [loading, setLoading] = useState(!auth?.user);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [formData, setFormData] = useState({
    username: auth?.user?.username ?? "",
    phoneNumber: auth?.user?.phone_number ?? "",
    schoolName: auth?.user?.school_name ?? "",
    governorate: auth?.user?.governorate ?? "",
  });

  const loadChaptersCount = useCallback(async () => {
    try {
      const res = await axiosPrivate.get(
        `${baseURL}/learning/user-chapters-count`
      );
      setChaptersCount(res.data?.count ?? 0);
    } catch (err) {
      console.error(err);
    }
  }, [axiosPrivate]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!auth?.accessToken) {
        setLoading(false);
        return;
      }

      if (auth?.user) {
        setProfile(auth.user);
        setFormData({
          username: auth.user.username ?? "",
          phoneNumber: auth.user.phone_number ?? "",
          schoolName: auth.user.school_name ?? "",
          governorate: auth.user.governorate ?? "",
        });
        loadChaptersCount();
        setLoading(false);
        return;
      }

      try {
        const res = await axiosPrivate.get(`${baseURL}/${DATAUSER}`);
        const userData = res.data?.data ?? res.data;
        setProfile(userData);
        setAuth((prev) => ({ ...prev, user: userData }));
        setFormData({
          username: userData.username ?? "",
          phoneNumber: userData.phone_number ?? "",
          schoolName: userData.school_name ?? "",
          governorate: userData.governorate ?? "",
        });
        loadChaptersCount();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [auth, axiosPrivate, loadChaptersCount, setAuth]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImageFile(file);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const body = new FormData();
      body.append("username", formData.username);
      body.append("phoneNumber", formData.phoneNumber || "");
      body.append("schoolName", formData.schoolName || "");
      body.append("governorate", formData.governorate || "");
      if (profileImageFile) {
        body.append("profileImage", profileImageFile);
      }

      const response = await axiosPrivate.put(
        `${baseURL}/auth/update-profile`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const updatedUser = response.data?.data ?? response.data;
      setProfile(updatedUser);
      setAuth((prev) => ({ ...prev, user: updatedUser }));
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setProfileImageFile(null);
      toast.success("تم تحديث البيانات بنجاح");
      setShowModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "حدث خطأ أثناء حفظ البيانات");
    } finally {
      setSaving(false);
    }
  };

  const profileImageSrc =
    resolveMediaUrl(profile?.profileImage) ||
    resolveMediaUrl(profile?.profile_image) ||
    Anonymath;

  if (!auth?.accessToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <Header />
      <div className="profile-page">
        <Container className="profile-container py-5">
          {loading ? (
            <p className="text-center text-muted">جاري تحميل البيانات...</p>
          ) : (
            <>
              <Row className="justify-content-center mb-4">
                <Col lg={10}>
                  <div className="personal-info-card">
                    <Row className="align-items-center g-4">
                      <Col md={4} className="text-center">
                        <img
                          src={profileImageSrc}
                          alt="Profile"
                          className="profile-image-circle"
                          onError={(e) => {
                            e.currentTarget.src = Anonymath;
                          }}
                        />
                      </Col>
                      <Col md={8} className="text-md-end text-center">
                        <h2 className="profile-name mb-2">
                          {profile?.username || "المستخدم"}
                        </h2>
                        <p className="profile-email mb-2">
                          <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                          {profile?.email || "غير متوفر"}
                        </p>
                        <p className="profile-year mb-0">
                          {profile?.academic_year_id
                            ? `السنة الدراسية: ${profile.academic_year_id}`
                            : "السنة الدراسية غير محددة"}
                        </p>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Row className="statistics-cards justify-content-center g-3 mb-4">
                <Col sm={6} lg={3}>
                  <div className="stat-card h-100">
                    <div className="stat-icon">
                      <FontAwesomeIcon icon={faWallet} />
                    </div>
                    <div className="stat-value">{profile?.credit ?? 0}</div>
                    <div className="stat-label">رصيد (جنيه)</div>
                  </div>
                </Col>
                <Col sm={6} lg={3}>
                  <div className="stat-card h-100">
                    <div className="stat-icon">
                      <FontAwesomeIcon icon={faBook} />
                    </div>
                    <div className="stat-value">{chaptersCount}</div>
                    <div className="stat-label">وحدة مشتراة</div>
                  </div>
                </Col>
                <Col sm={6} lg={3}>
                  <div className="stat-card h-100">
                    <div className="stat-icon">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                    </div>
                    <div className="stat-value text-truncate px-2">
                      {profile?.governorate || "—"}
                    </div>
                    <div className="stat-label">المحافظة</div>
                  </div>
                </Col>
                <Col sm={6} lg={3}>
                  <div className="stat-card h-100">
                    <div className="stat-icon">
                      <FontAwesomeIcon icon={faSchool} />
                    </div>
                    <div className="stat-value text-truncate px-2">
                      {profile?.school_name || "—"}
                    </div>
                    <div className="stat-label">المدرسة</div>
                  </div>
                </Col>
              </Row>

              <Row className="justify-content-center">
                <Col lg={10}>
                  <div className="account-data-card">
                    <h4 className="account-data-title mb-4">بيانات الحساب</h4>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="data-field">
                          <label className="data-label">
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            الاسم
                          </label>
                          <p className="data-value">
                            {profile?.username || "غير متوفر"}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="data-field">
                          <label className="data-label">
                            <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                            الإيميل
                          </label>
                          <p className="data-value">
                            {profile?.email || "غير متوفر"}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="data-field">
                          <label className="data-label">
                            <FontAwesomeIcon icon={faPhone} className="me-1" />
                            رقم الهاتف
                          </label>
                          <p className="data-value">
                            {profile?.phone_number || "غير متوفر"}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="data-field">
                          <label className="data-label">
                            <FontAwesomeIcon icon={faPhone} className="me-1" />
                            رقم هاتف ولي الأمر
                          </label>
                          <p className="data-value">
                            {profile?.parent_phone_number || "غير متوفر"}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="data-field">
                          <label className="data-label">
                            <FontAwesomeIcon icon={faSchool} className="me-1" />
                            المدرسة
                          </label>
                          <p className="data-value">
                            {profile?.school_name || "غير محددة"}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="data-field">
                          <label className="data-label">
                            <FontAwesomeIcon
                              icon={faMapMarkerAlt}
                              className="me-1"
                            />
                            المحافظة
                          </label>
                          <p className="data-value">
                            {profile?.governorate || "غير محددة"}
                          </p>
                        </div>
                      </Col>
                    </Row>

                    <div className="text-center mt-4 pt-2">
                      <Button
                        className="edit-button px-5"
                        onClick={() => setShowModal(true)}
                      >
                        تعديل البيانات
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Container>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>تعديل البيانات الشخصية</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSave} className="profile-form">
              <Form.Group className="mb-3">
                <Form.Label htmlFor="profileImage">صورة الملف الشخصي</Form.Label>
                <Form.Control
                  id="profileImage"
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="username">الاسم</Form.Label>
                <Form.Control
                  id="username"
                  type="text"
                  name="username"
                  autoComplete="name"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="phoneNumber">رقم الهاتف</Form.Label>
                <Form.Control
                  id="phoneNumber"
                  type="text"
                  name="phoneNumber"
                  autoComplete="tel"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="schoolName">المدرسة</Form.Label>
                <Form.Control
                  id="schoolName"
                  type="text"
                  name="schoolName"
                  autoComplete="organization"
                  value={formData.schoolName}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="governorate">المحافظة</Form.Label>
                <Form.Control
                  id="governorate"
                  type="text"
                  name="governorate"
                  autoComplete="address-level1"
                  value={formData.governorate}
                  onChange={handleChange}
                />
              </Form.Group>
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  إلغاء
                </Button>
                <Button variant="primary" type="submit" disabled={saving}>
                  {saving ? "جاري الحفظ..." : "حفظ"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}
